// Plain-text (.txt) backup export of ALL care programs of a patient.
// Pure client-side: builds a readable Spanish report and triggers download.

const LINE = "=".repeat(72);
const THIN = "-".repeat(72);

const INC_LABELS = {
  caida: "Caida",
  reaccion: "Reaccion adversa a medicamento",
  dolor: "Crisis de dolor",
  svitales: "Cambio brusco en signos vitales",
  agitacion: "Agitacion / conducta inadecuada",
  otra: "Otra incidencia",
};

const PD_APPEARANCE = {
  claro: "Claro", turbio: "Turbio", hematico: "Hematico", fibrina: "Con fibrina",
};

const PEG_TOLERANCE = {
  bien: "Bien tolerada", nausea: "Nausea", vomito: "Vomito",
  regurgitacion: "Regurgitacion/reflujo", distension: "Distension abdominal", diarrea: "Diarrea",
};

const PEG_METHOD = { bolo: "Bolo", gravedad: "Gravedad", bomba: "Bomba" };

const PEG_STOMA = {
  sano: "Sano", enrojecido: "Enrojecido", granuloma: "Granuloma",
  fuga: "Fuga periestomal", secrecion: "Secrecion serosa",
  secrecion_purulenta: "Secrecion purulenta", dolor: "Dolor",
};

const PEG_PATENCY = {
  permeable: "Permeable", lenta: "Pasa lento", obstruida: "OBSTRUIDA",
  salida_accidental: "SALIDA ACCIDENTAL",
};

function yn(v) {
  if (v === true) return "Si";
  if (v === false) return "No";
  return "-";
}

function val(v) {
  return v === null || v === undefined || v === "" ? "-" : String(v);
}

// Section only if it has content; returns [] otherwise.
function programToText(p, idx) {
  const s = p.shift || {};
  const c = p.closing || {};
  const out = [];

  out.push(LINE);
  out.push(`PROGRAMA #${idx + 1}  —  ${val(s.date)}  ${val(s.start_time)}${s.end_time ? ` a ${s.end_date && s.end_date !== s.date ? s.end_date + " " : ""}${s.end_time}` : ""}`);
  out.push(LINE);
  out.push(`Enfermera(s) del turno: ${(s.nurses || []).filter(Boolean).join(", ") || "-"}`);
  out.push(`Registrado por: ${val(p.owner_name)}`);
  if (p.created_at) out.push(`Creado: ${p.created_at}`);
  if (p.updated_at) out.push(`Ultima modificacion: ${p.updated_at}`);

  // Interventions
  const ivs = p.interventions || [];
  out.push("");
  out.push(`INTERVENCIONES (${ivs.length})`);
  out.push(THIN);
  if (!ivs.length) out.push("  (sin intervenciones registradas)");
  ivs.forEach((iv) => {
    out.push(
      `  ${val(iv.start_time)}${iv.end_time ? `-${iv.end_time}` : ""}  [${val(iv.category)}] ${val(iv.name)} (${iv.duration_minutes || 0} min)${iv.nurse ? ` — ${iv.nurse}` : ""}`
    );
    if (iv.notes) out.push(`      Notas: ${iv.notes}`);
  });

  // Materials
  const mats = p.materials || [];
  if (mats.length) {
    out.push("");
    out.push(`MATERIAL UTILIZADO (${mats.length})`);
    out.push(THIN);
    mats.forEach((m) => out.push(`  ${m.quantity || 0} ${m.unit || "pz"}  ${val(m.name)}`));
  }

  // Closing / clinical record
  out.push("");
  out.push("CIERRE DEL TURNO");
  out.push(THIN);
  out.push(`  Estado general: ${val(c.condition)}   Estado emocional: ${val(c.emotion)}   Dolor EVA: ${c.eva_pain ?? "-"}/10`);

  // Vitals
  const v = c.vitals || {};
  const readings = (v.readings || []).filter((r) => r.ta || r.fc || r.temp || r.spo2);
  if (readings.length || v.ta || v.fc || v.temp || v.spo2 || v.weight_kg) {
    out.push("");
    out.push("  SIGNOS VITALES:");
    if (v.weight_kg) out.push(`    Peso: ${v.weight_kg} kg`);
    if (readings.length) {
      readings.forEach((r) =>
        out.push(`    ${val(r.time)}  TA ${val(r.ta)}  FC ${val(r.fc)}  Temp ${val(r.temp)}  SpO2 ${val(r.spo2)}`)
      );
    } else {
      out.push(`    TA ${val(v.ta)}  FC ${val(v.fc)}  Temp ${val(v.temp)}  SpO2 ${val(v.spo2)}`);
    }
  }

  // Nutrition
  const n = c.nutrition || {};
  if (Object.values(n).some((x) => x !== "" && x !== null && x !== undefined)) {
    out.push("");
    out.push("  ALIMENTACION:");
    out.push(`    Via: ${val(n.via)}  Tolerancia: ${val(n.tolerance)}  Ingesta: ${val(n.intake)}  Apetito: ${val(n.appetite)}  Dieta: ${val(n.diet_type)}`);
    if (n.breakfast || n.lunch || n.dinner) {
      out.push(`    Raciones — Desayuno: ${val(n.breakfast)}  Comida: ${val(n.lunch)}  Cena: ${val(n.dinner)}`);
    }
    if (n.water_oral_ml) out.push(`    Agua oral: ${n.water_oral_ml} ml`);
    if (n.nausea !== null || n.vomit !== null) out.push(`    Nausea: ${yn(n.nausea)}  Vomito: ${yn(n.vomit)}`);
  }

  // Elimination
  const e = c.elimination || {};
  if (Object.values(e).some((x) => x !== "" && x !== null && x !== undefined)) {
    out.push("");
    out.push("  ELIMINACION:");
    out.push(`    Orina — Metodo: ${val(e.urine_method)}  Aspecto: ${val(e.urine_appearance)}  Volumen: ${val(e.urine_volume)}  Veces: ${val(e.urine_times)}  Cantidad: ${val(e.urine_amount_ml)} ml`);
    out.push(`    Evacuacion — Presente: ${yn(e.stool_present)}  Consistencia: ${val(e.stool_consistency)}  Aspecto: ${val(e.stool_appearance)}  Metodo: ${val(e.stool_method)}  Veces: ${val(e.stool_times)}  Bristol: ${val(e.bristol)}`);
    if (e.vomit_ml || e.drain_ml) out.push(`    Otros egresos — Vomito: ${val(e.vomit_ml)} ml  Drenajes: ${val(e.drain_ml)} ml`);
  }

  // Medications given
  const meds = c.medications_given || [];
  if (meds.length) {
    out.push("");
    out.push(`  MEDICAMENTOS ADMINISTRADOS (${meds.length}):`);
    meds.forEach((m) => {
      out.push(`    ${val(m.time)}  ${val(m.name)}  ${val(m.dose)}  via ${val(m.via)}  ${m.indication === "prn" ? "PRN" : "Rx"}${m.administered_by ? ` — ${m.administered_by}` : ""}`);
      if (m.observations) out.push(`      Obs: ${m.observations}`);
    });
  }

  // Sleep
  const sl = c.sleep || {};
  if ((sl.hours || []).length || sl.observations) {
    out.push("");
    out.push(`  SUENO: ${(sl.hours || []).length} hora(s) dormida(s)${(sl.hours || []).length ? ` (horas: ${(sl.hours || []).join(", ")})` : ""}`);
    if (sl.observations) out.push(`    Obs: ${sl.observations}`);
  }

  // Morse
  const mo = c.morse || {};
  const moVals = Object.values(mo).filter((x) => x !== null && x !== undefined);
  if (moVals.length) {
    const total = moVals.reduce((a, b) => a + (Number(b) || 0), 0);
    out.push("");
    out.push(`  ESCALA DE MORSE (riesgo de caidas): ${total} pts (${total >= 45 ? "ALTO" : total >= 25 ? "medio" : "bajo"})`);
  }

  // Peritoneal dialysis
  const pd = c.peritoneal_dialysis || {};
  const pdEx = pd.exchanges || [];
  if (pdEx.length || pd.morning_weight_kg || pd.observations) {
    out.push("");
    out.push(`  DIALISIS PERITONEAL DPCA (${pdEx.length} recambios):`);
    if (pd.morning_weight_kg) out.push(`    Peso matutino: ${pd.morning_weight_kg} kg`);
    let inf = 0, drn = 0;
    pdEx.forEach((ex) => {
      const i2 = Number(ex.infused_ml) || 0;
      const d2 = Number(ex.drained_ml) || 0;
      inf += i2; drn += d2;
      const uf = i2 || d2 ? d2 - i2 : null;
      out.push(`    #${ex.number || "?"} ${val(ex.start_time)}  Bolsa ${val(ex.concentration)}  Inf ${val(ex.infused_ml)} ml  Dren ${val(ex.drained_ml)} ml  UF ${uf === null ? "-" : (uf > 0 ? "+" : "") + uf} ml  Aspecto: ${PD_APPEARANCE[ex.appearance] || val(ex.appearance)}${ex.notes ? `  — ${ex.notes}` : ""}`);
    });
    if (pdEx.length) out.push(`    Subtotal turno — Infundido: ${inf} ml  Drenado: ${drn} ml  UF: ${drn - inf > 0 ? "+" : ""}${drn - inf} ml`);
    if (pd.observations) out.push(`    Obs DPCA: ${pd.observations}`);
  }

  // Gastrostomy (PEG)
  const g = c.gastrostomy || {};
  const feeds = g.feedings || [];
  const tc = g.tube_care || {};
  const tcHas = (tc.stoma_status || []).length || tc.external_length_cm || tc.patency || tc.tube_rotated != null || tc.stoma_cleaned != null || tc.notes;
  if (feeds.length || tcHas || g.weight_kg || g.observations) {
    out.push("");
    out.push(`  GASTROSTOMIA PEG (${feeds.length} tomas):`);
    if (g.weight_kg) out.push(`    Peso: ${g.weight_kg} kg`);
    let formula = 0, water = 0;
    feeds.forEach((f) => {
      const fv = Number(f.volume_ml) || 0;
      const wv = (Number(f.flush_before_ml) || 0) + (Number(f.flush_after_ml) || 0);
      formula += fv; water += wv;
      out.push(`    #${f.number || "?"} ${val(f.time)}  ${val(f.formula_type)}  ${val(f.volume_ml)} ml  ${PEG_METHOD[f.method] || val(f.method)}  Residuo ${val(f.residual_ml)} ml  Agua ${wv || "-"} ml  Tolerancia: ${PEG_TOLERANCE[f.tolerance] || val(f.tolerance)}  Cabecera 30-45: ${yn(f.head_elevated)}${f.notes ? `  — ${f.notes}` : ""}`);
    });
    if (feeds.length) out.push(`    Subtotal turno — Formula: ${formula} ml  Agua: ${water} ml  Total enteral: ${formula + water} ml`);
    if (tcHas) {
      const parts = [];
      if ((tc.stoma_status || []).length) parts.push(`Estoma: ${(tc.stoma_status || []).map((x) => PEG_STOMA[x] || x).join(", ")}`);
      if (tc.external_length_cm) parts.push(`Marca externa: ${tc.external_length_cm} cm`);
      if (tc.patency) parts.push(`Permeabilidad: ${PEG_PATENCY[tc.patency] || tc.patency}`);
      if (tc.tube_rotated != null) parts.push(`Sonda rotada: ${yn(tc.tube_rotated)}`);
      if (tc.stoma_cleaned != null) parts.push(`Limpieza estoma: ${yn(tc.stoma_cleaned)}`);
      out.push(`    Cuidados de sonda — ${parts.join("  ·  ")}`);
      if (tc.notes) out.push(`    Notas sonda: ${tc.notes}`);
    }
    if (g.observations) out.push(`    Obs PEG: ${g.observations}`);
  }

  // Incidents
  const inc = c.incidents || {};
  const activeInc = Object.entries(inc).filter(([, x]) => x === true).map(([k]) => INC_LABELS[k] || k);
  if (activeInc.length || c.incidents_desc) {
    out.push("");
    out.push(`  INCIDENCIAS: ${activeInc.join(", ") || "-"}`);
    if (c.incidents_desc) out.push(`    Descripcion: ${c.incidents_desc}`);
  }

  // Pending + observations + closing
  const pending = c.pending || [];
  if (pending.length) {
    out.push("");
    out.push("  PENDIENTES PARA EL SIGUIENTE TURNO:");
    pending.forEach((x) => out.push(`    - ${x}`));
  }
  if (c.observations) {
    out.push("");
    out.push(`  OBSERVACIONES GENERALES: ${c.observations}`);
  }
  if (c.closing_nurse || c.closing_time) {
    out.push("");
    out.push(`  Cierre — Enfermera: ${val(c.closing_nurse)}  Hora: ${val(c.closing_time)}`);
  }

  out.push("");
  return out;
}

// Build and download the .txt backup. Returns the number of programs exported.
export function exportPatientTxt(programs, patientName) {
  const name = (patientName || "").trim();
  const list = (programs || [])
    .filter((p) => ((p.shift?.patient_name || "").trim() === name))
    .sort((a, b) =>
      `${a.shift?.date || ""} ${a.shift?.start_time || ""}`.localeCompare(
        `${b.shift?.date || ""} ${b.shift?.start_time || ""}`
      )
    );
  if (!list.length) return 0;

  const now = new Date();
  const lines = [];
  lines.push(LINE);
  lines.push("CIUDAD ENFERMERIA — RESPALDO DE PROGRAMAS DE CUIDADOS");
  lines.push(LINE);
  lines.push(`Paciente: ${name}`);
  lines.push(`Programas incluidos: ${list.length}`);
  lines.push(`Periodo: ${list[0].shift?.date || "-"} a ${list[list.length - 1].shift?.date || "-"}`);
  lines.push(`Generado: ${now.toLocaleString("es-MX")}`);
  lines.push("");

  list.forEach((p, i) => lines.push(...programToText(p, i)));

  lines.push(LINE);
  lines.push(`FIN DEL RESPALDO — ${list.length} programa(s)`);
  lines.push(LINE);

  const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  const safe = name.replace(/[^a-z0-9]+/gi, "_").slice(0, 40) || "paciente";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `respaldo_${safe}_${stamp}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
  return list.length;
}
